// ParticleBlast - A utility for creating particle animations
// Can be instantiated multiple times on a page

function getRGBComponentFromHex(hex) {
  const [r, g, b] = hex
    .toLowerCase()
    .match(/[a-f0-9]{2}/g)
    .map((x) => parseInt(x, 16))
  return [r, g, b]
}

function getRGBComponentsFromColorName(colorName) {
  const d = document.documentElement
  d.style.setProperty('color', colorName)
  const color = getComputedStyle(d).getPropertyValue('color')

  // color is of format rgb(r, g, b)
  const [r, g, b] = color.match(/\d+/g).map(Number)
  return [r, g, b]
}

class ParticleBlast {
  constructor(options = {}) {
    this.isActive = false
    this.lastTime = 0
    this.particles = []
    this.particlePointer = 0
    this.MAX_PARTICLES = options.maxParticles || 500
    this.PARTICLE_NUM_RANGE = options.particleNumRange || {min: 25, max: 50}
    this.PARTICLE_SIZE_RANGE = options.particleSizeRange || {min: 3, max: 10}
    this.PARTICLE_ALPHA_START = options.particleAlphaStart || 0.5
    this.PARTICLE_ALPHA_FADEOUT = options.particleAlphaFadeout || 0.99
    this.PARTICLE_SIZE_FADEOUT = options.particleSizeFadeout || 0.975
    this.PARTICLE_VELOCITY_RANGE = options.particleVelocityRange || {
      x: [-1, 1],
      y: [-3.5, -1.5],
    }
    this.effect = options.effect || 2

    // These will be set later when initialize is called
    this.ctx = null
    this.w = 0
    this.h = 0
    this.imageRect = null

    // Bind methods to maintain context
    this.loop = this.loop.bind(this)
  }

  // Initialize with canvas context and target element rectangle
  initialize(ctx, canvasWidth, canvasHeight, imageRect) {
    this.ctx = ctx
    this.w = canvasWidth
    this.h = canvasHeight
    this.imageRect = imageRect
    this.isActive = true
    this.loop()

    return this // For chaining
  }

  // Stop animation
  stop() {
    this.isActive = false
    return this
  }

  // Start or restart animation
  start() {
    if (!this.isActive) {
      this.isActive = true
      this.loop()
    }
    return this
  }

  random(min, max) {
    if (!max) {
      max = min
      min = 0
    }
    return min + ~~(Math.random() * (max - min + 1))
  }

  // Spawn particles with given colors
  spawnParticles(colors) {
    if (!this.ctx || !this.imageRect) {
      console.warn('ParticleBlast: Cannot spawn particles - not initialized')
      return this
    }

    var numParticles = this.random(
      this.PARTICLE_NUM_RANGE.min,
      this.PARTICLE_NUM_RANGE.max
    )
    for (var i = numParticles; i--; ) {
      const pos = {
        left: this.imageRect.left + this.random(0, this.imageRect.width),
        top: this.imageRect.top + this.random(0, this.imageRect.height),
      }
      const randomColor = colors[Math.floor(Math.random() * colors.length)]
      const rgb = randomColor.startsWith('#')
        ? getRGBComponentFromHex(randomColor)
        : getRGBComponentsFromColorName(randomColor)

      this.particles[this.particlePointer] = this.createParticle(
        pos.left,
        pos.top,
        rgb
      )
      this.particlePointer = (this.particlePointer + 1) % this.MAX_PARTICLES
    }

    return this
  }

  createParticle(x, y, color) {
    var p = {
      x: x,
      y: y + 10,
      alpha: this.PARTICLE_ALPHA_START,
      color: color,
    }

    p.size = this.random(
      this.PARTICLE_SIZE_RANGE.min,
      this.PARTICLE_SIZE_RANGE.max
    )
    p.drag = 0.92
    p.vx = this.random(-3, 3)
    p.vy = this.random(-3, 3)
    p.wander = 0.15
    p.theta = (this.random(0, 360) * Math.PI) / 180

    return p
  }

  // Effect based on Soulwire's demo: http://codepen.io/soulwire/pen/foktm
  effect2(particle) {
    particle.x += particle.vx
    particle.y += particle.vy
    particle.vx *= particle.drag
    particle.vy *= particle.drag
    particle.theta += this.random(-0.5, 0.5)
    particle.vx += Math.sin(particle.theta) * 0.1
    particle.vy += Math.cos(particle.theta) * 0.1
    particle.size *= this.PARTICLE_SIZE_FADEOUT
    particle.alpha *= this.PARTICLE_ALPHA_FADEOUT

    this.ctx.fillStyle =
      'rgba(' +
      particle.color[0] +
      ',' +
      particle.color[1] +
      ',' +
      particle.color[2] +
      ',' +
      particle.alpha +
      ')'
    this.ctx.beginPath()
    this.ctx.arc(
      Math.round(particle.x - 1),
      Math.round(particle.y - 1),
      particle.size,
      0,
      2 * Math.PI
    )
    this.ctx.fill()
  }

  // For forward compatibility
  effect1(particle) {
    // Add implementation if needed
    this.effect2(particle)
  }

  drawParticles(timeDelta) {
    var particle
    for (var i = this.particles.length; i--; ) {
      particle = this.particles[i]
      if (!particle || particle.alpha < 0.01 || particle.size <= 0.5) {
        continue
      }

      if (this.effect === 1) {
        this.effect1(particle)
      } else if (this.effect === 2) {
        this.effect2(particle)
      }
    }
  }

  loop() {
    if (!this.isActive) return
    this.ctx.clearRect(0, 0, this.w, this.h)

    // get the time past the previous frame
    var current_time = new Date().getTime()
    if (!this.lastTime) this.lastTime = current_time
    var dt = (current_time - this.lastTime) / 1000
    this.lastTime = current_time

    this.drawParticles(dt)
    requestAnimationFrame(this.loop)
  }
}

export default ParticleBlast
